import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const THEME = "#8b1634";
const THEME_LIGHT = "rgb(239, 226, 231)";
const THEME_BORDER = "rgba(139, 22, 52, 0.18)";

export const THEME_YN_CHOICES = [
  { value: "Y", label: "Y" },
  { value: "N", label: "N" },
];

const MENU_Z_INDEX = 1060;
const MENU_GAP = 2;

function estimateMenuHeight(choiceCount, fontSize) {
  const rowH = Math.max(30, fontSize + 16);
  return choiceCount * rowH + 4;
}

export default function ThemeYnSelect({
  value = "",
  onChange,
  style,
  placeholder = " ",
  fontSize = 13,
  choices = THEME_YN_CHOICES,
  emptyColor = THEME,
}) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const [hover, setHover] = useState(null);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const updateMenuPosition = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;

    const rect = btn.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight || estimateMenuHeight(choices.length, fontSize);
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const shouldOpenUp = spaceBelow < menuHeight + MENU_GAP + 8 && spaceAbove >= spaceBelow;

    setOpenUp(shouldOpenUp);
    setMenuPos({
      left: rect.left,
      width: rect.width,
      top: shouldOpenUp ? Math.max(8, rect.top - menuHeight - MENU_GAP) : rect.bottom + MENU_GAP,
    });
  }, [choices.length, fontSize]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    updateMenuPosition();
  }, [open, updateMenuPosition]);

  useLayoutEffect(() => {
    if (!open || !menuRef.current) return undefined;
    const id = requestAnimationFrame(() => updateMenuPosition());
    return () => cancelAnimationFrame(id);
  }, [open, choices, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;

    const onDocMouseDown = (e) => {
      if (rootRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onReposition = () => updateMenuPosition();

    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);

    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, updateMenuPosition]);

  const pick = (next) => {
    onChange(next);
    setOpen(false);
    setHover(null);
  };

  const optionStyle = (optValue) => {
    const selected = value === optValue;
    const highlighted = hover === optValue;
    if (highlighted) {
      return { background: THEME, color: "#fff", fontWeight: 600 };
    }
    if (selected) {
      return { background: THEME_LIGHT, color: THEME, fontWeight: 600 };
    }
    return { background: "#fff", color: "#1a202c", fontWeight: 400 };
  };

  const displayLabel = choices.find((c) => c.value === value)?.label ?? value;

  const menuBoxStyle = {
    margin: 0,
    padding: 0,
    listStyle: "none",
    background: "#fff",
    border: `1px solid ${THEME_BORDER}`,
    borderRadius: 6,
    boxShadow: "0 4px 14px rgba(139, 22, 52, 0.18)",
    overflow: "hidden",
  };

  const menu =
    open && menuPos
      ? createPortal(
          <ul
            ref={menuRef}
            role="listbox"
            style={{
              ...menuBoxStyle,
              position: "fixed",
              left: menuPos.left,
              width: menuPos.width,
              top: menuPos.top,
              zIndex: MENU_Z_INDEX,
            }}
          >
            {choices.map((opt, idx) => (
              <li key={opt.value} role="option" aria-selected={value === opt.value}>
                <button
                  type="button"
                  onMouseEnter={() => setHover(opt.value)}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => pick(opt.value)}
                  style={{
                    width: "100%",
                    border: "none",
                    borderTop: idx > 0 ? `1px solid ${THEME_BORDER}` : "none",
                    padding: "7px 10px",
                    fontSize,
                    textAlign: "left",
                    cursor: "pointer",
                    ...optionStyle(opt.value),
                  }}
                >
                  {opt.label}
                </button>
              </li>
            ))}
          </ul>,
          document.body
        )
      : null;

  return (
    <div ref={rootRef} style={{ position: "relative", width: "100%", ...style }}>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          border: "none",
          outline: "none",
          background: "transparent",
          cursor: "pointer",
          fontSize,
          color: value ? "#1a202c" : emptyColor,
          fontWeight: value ? 500 : 600,
          padding: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 4,
        }}
      >
        <span>{value ? displayLabel : placeholder}</span>
        <span style={{ fontSize: 9, color: THEME, lineHeight: 1 }} aria-hidden>
          {open && openUp ? "▲" : "▼"}
        </span>
      </button>
      {menu}
    </div>
  );
}
